import { faChevronDown, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import SimpleTaskList from 'components/tasks/SimpleTaskList';
import TemplateNewTaskCard from 'components/tasks/TemplateNewTaskCard';
import useApi from 'helpers/api';
import useNotification from 'hooks/useNotification';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';
import TaskTemplateItemService from 'services/TaskTemplateItem.service';

const TemplateTaskSection = forwardRef(
  (
    {
      taskTemplateId = null, // usado apenas para edição
      onCountChange,
      initialTasks = [],
      tasksLoaded = false
    },
    ref
  ) => {
    const api = useApi();
    const { showSuccessNotification, showErrorNotification } =
      useNotification();
    const [tasks, setTasks] = useState([]);
    const [agents, setAgents] = useState([]);
    const [showNewTaskForm, setShowNewTaskForm] = useState(false);
    const [isGroupTitle, setIsGroupTitle] = useState(false);
    const [nextTaskId, setNextTaskId] = useState(1000); // Para gerar IDs únicos temporários
    const [isInitialized, setIsInitialized] = useState(false);
    const [isCreatingTask, setIsCreatingTask] = useState(false);

    useEffect(() => {
      loadInitialData();
    }, []);

    // Effect para atualizar tasks quando initialTasks mudar
    useEffect(() => {
      if (tasksLoaded && !isInitialized) {
        console.log('Atualizando tasks com initialTasks:', initialTasks);

        // Sincroniza os campos de prazo nas tasks
        const syncedTasks = (initialTasks || []).map(task => {
          const deadlineValue =
            task.deadlineDays !== undefined
              ? task.deadlineDays
              : task.days_to_complete;
          // Garantir que valores vazios sejam tratados como null
          const normalizedValue =
            deadlineValue === null ||
            deadlineValue === undefined ||
            deadlineValue === ''
              ? null
              : deadlineValue;
          return {
            ...task,
            deadlineDays: normalizedValue,
            days_to_complete: normalizedValue
          };
        });

        setTasks(syncedTasks);

        // Atualiza o nextTaskId baseado nos IDs existentes
        if (initialTasks && initialTasks.length > 0) {
          const maxId = Math.max(...initialTasks.map(t => t.id || 0), 999);
          setNextTaskId(maxId + 1);
        }

        setIsInitialized(true);
      }
    }, [tasksLoaded, initialTasks, isInitialized]);

    useEffect(() => {
      if (onCountChange) {
        const totalCount = tasks.length;
        onCountChange(totalCount);
      }
    }, [tasks, onCountChange]);

    const loadInitialData = async () => {
      try {
        const agentsResponse = await api.agents.listSummaryData();
        setAgents(agentsResponse || []);
      } catch (error) {
        showErrorNotification('Erro ao carregar agentes');
        setAgents([]);
      }
    };

    const handleAddTaskClick = () => {
      setIsGroupTitle(false);
      setShowNewTaskForm(true);
    };

    const handleGroupTitleClick = () => {
      setIsGroupTitle(true);
      setShowNewTaskForm(true);
    };

    const handleCancelNewTask = () => {
      setShowNewTaskForm(false);
    };

    // Função para manter o formulário aberto após adicionar uma tarefa
    const handleKeepFormOpen = () => {
      // Manter o formulário aberto e garantir que o foco seja mantido
      // O TemplateNewTaskCard já chama startNewTask() que limpa os dados
      console.log('Formulário mantido aberto após adicionar tarefa');
    };

    const handleTaskAdded = async newTask => {
      setIsCreatingTask(true);

      try {
        // Se estamos editando um template existente, criar a tarefa na API
        if (taskTemplateId) {
          const taskData = {
            title: newTask.title,
            isGroupTitle: newTask.isGroupTitle || false,
            description: newTask.description || '',
            deadlineDays:
              newTask.deadlineDays === null ||
              newTask.deadlineDays === undefined ||
              newTask.deadlineDays === ''
                ? null
                : parseInt(newTask.deadlineDays),
            status: newTask.status || false,
            agent: newTask.agent ? { id: newTask.agent.id } : undefined
          };

          const createdTask = await TaskTemplateItemService.create(
            taskTemplateId,
            taskData
          );

          if (createdTask && !createdTask.error) {
            const deadlineDays =
              createdTask.deadlineDays === null ||
              createdTask.deadlineDays === undefined ||
              createdTask.deadlineDays === ''
                ? null
                : createdTask.deadlineDays;

            const formattedTask = {
              id: createdTask.id,
              title: createdTask.title,
              description: createdTask.description || '',
              isGroupTitle: createdTask.isGroupTitle || false,
              deadlineDays: deadlineDays,
              days_to_complete: deadlineDays,
              agent: createdTask.agent
                ? {
                    id: createdTask.agent.id,
                    firstName: createdTask.agent.firstName || '',
                    lastName: createdTask.agent.lastName || '',
                    name: `${createdTask.agent.firstName || ''} ${
                      createdTask.agent.lastName || ''
                    }`.trim()
                  }
                : null,
              status: createdTask.status || false,
              isTemporary: false
            };

            setTasks(prevTasks => [...prevTasks, formattedTask]);
            showSuccessNotification('Tarefa adicionada com sucesso!');
          } else {
            throw new Error(createdTask?.message || 'Erro ao criar tarefa');
          }
        } else {
          // Modo de criação - apenas adiciona ao estado local
          const deadlineValue =
            newTask.deadlineDays !== undefined
              ? newTask.deadlineDays
              : newTask.days_to_complete;
          // Garantir que valores vazios sejam tratados como null
          const normalizedValue =
            deadlineValue === null ||
            deadlineValue === undefined ||
            deadlineValue === ''
              ? null
              : deadlineValue;
          const taskWithId = {
            ...newTask,
            id: nextTaskId,
            deadlineDays: normalizedValue,
            days_to_complete: normalizedValue,
            isTemporary: true
          };

          setTasks(prevTasks => [...prevTasks, taskWithId]);
          setNextTaskId(prev => prev + 1);
        }

        // Garantir que o formulário permaneça aberto após adicionar a tarefa
        // O TemplateNewTaskCard já chama startNewTask() que limpa os dados
      } catch (error) {
        showErrorNotification(error.message || 'Erro ao adicionar tarefa');
      } finally {
        setIsCreatingTask(false);
      }
    };

    const handleTaskUpdate = async (
      updatedTask,
      suppressNotification = false
    ) => {
      try {
        // Encontra a tarefa original no estado
        const originalTask = tasks.find(task => task.id === updatedTask.id);
        if (!originalTask) {
          return;
        }

        // Verifica se houve alterações
        const originalAgentId = originalTask.agent?.id || null;
        const updatedAgentId = updatedTask.agent?.id || null;

        // Função para comparar valores de prazo considerando null
        const compareDeadlineValues = (val1, val2) => {
          const normalizedVal1 =
            val1 === null || val1 === undefined || val1 === '' ? null : val1;
          const normalizedVal2 =
            val2 === null || val2 === undefined || val2 === '' ? null : val2;
          return normalizedVal1 === normalizedVal2;
        };

        const hasChanges =
          originalTask.title !== updatedTask.title ||
          originalTask.isGroupTitle !== updatedTask.isGroupTitle ||
          originalTask.description !== updatedTask.description ||
          !compareDeadlineValues(
            originalTask.deadlineDays,
            updatedTask.deadlineDays
          ) ||
          !compareDeadlineValues(
            originalTask.days_to_complete,
            updatedTask.days_to_complete
          ) ||
          originalTask.status !== updatedTask.status ||
          originalAgentId !== updatedAgentId;

        if (!hasChanges) {
          return;
        }

        // Verifica se é uma task persistida
        if (taskTemplateId && updatedTask && !updatedTask.isTemporary) {
          const updateData = {
            title: updatedTask.title,
            isGroupTitle: updatedTask.isGroupTitle,
            description: updatedTask.description,
            deadlineDays: updatedTask.deadlineDays,
            status: updatedTask.status,
            agent:
              updatedTask.agent && updatedTask.agent.id
                ? { id: updatedTask.agent.id }
                : { id: null }
          };

          const response = await TaskTemplateItemService.update(
            taskTemplateId,
            updatedTask.id,
            updateData
          );

          if (response && (response.error || response.status >= 400)) {
            const errorMessage =
              response.userMessage ||
              response.description ||
              response.message ||
              'Erro ao atualizar tarefa';
            throw new Error(errorMessage);
          }

          if (!suppressNotification) {
            showSuccessNotification('Tarefa atualizada com sucesso!');
          }
        } else {
        }

        // Atualiza o estado local
        setTasks(prevTasks =>
          prevTasks.map(task => {
            if (task.id === updatedTask.id) {
              // Sincroniza os campos de prazo
              const syncedTask = { ...task, ...updatedTask };
              if (updatedTask.deadlineDays !== undefined) {
                syncedTask.days_to_complete = updatedTask.deadlineDays;
              }
              if (updatedTask.days_to_complete !== undefined) {
                syncedTask.deadlineDays = updatedTask.days_to_complete;
              }
              // Garantir que valores null sejam tratados corretamente
              if (
                syncedTask.deadlineDays === null ||
                syncedTask.deadlineDays === undefined ||
                syncedTask.deadlineDays === ''
              ) {
                syncedTask.deadlineDays = null;
                syncedTask.days_to_complete = null;
              }
              return syncedTask;
            }
            return task;
          })
        );
      } catch (error) {
        showErrorNotification('Erro ao atualizar tarefa');
      }
    };

    const handleTaskDelete = async (taskId, suppressNotification = false) => {
      try {
        // Encontra a task para verificar se é temporária
        const taskToDelete = tasks.find(task => task.id === taskId);

        // tem taskTemplateId e a task não é temporária, deleta na API
        if (taskTemplateId && taskToDelete && !taskToDelete.isTemporary) {
          await TaskTemplateItemService.delete(taskTemplateId, taskId);

          if (!suppressNotification) {
            showSuccessNotification('Tarefa removida com sucesso!');
          }
        }

        setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      } catch (error) {
        console.error('Erro ao remover tarefa:', error);
        showErrorNotification('Erro ao remover tarefa');
      }
    };

    const handleTaskReorder = async (taskId, oldIndex, newIndex) => {
      // Atualiza o estado local primeiro
      setTasks(prevTasks => {
        const newTasks = [...prevTasks];
        const [movedTask] = newTasks.splice(oldIndex, 1);
        newTasks.splice(newIndex, 0, movedTask);
        return newTasks;
      });

      try {
        // Encontra a task para verificar se é temporária
        const taskToReorder = tasks.find(task => task.id === taskId);

        // Se tem taskTemplateId e a task não é temporária, reordena na API
        if (taskTemplateId && taskToReorder && !taskToReorder.isTemporary) {
          const currentTasks = [...tasks];
          const [movedTask] = currentTasks.splice(oldIndex, 1);
          currentTasks.splice(newIndex, 0, movedTask);

          // Determina as tarefas acima e abaixo (apenas tasks não temporárias)
          const taskAbove = newIndex > 0 ? currentTasks[newIndex - 1] : null;
          const taskBelow =
            newIndex < currentTasks.length - 1
              ? currentTasks[newIndex + 1]
              : null;

          await TaskTemplateItemService.reorder(taskTemplateId, taskId, {
            taskAboveId:
              taskAbove && !taskAbove.isTemporary ? taskAbove.id : null,
            taskBelowId:
              taskBelow && !taskBelow.isTemporary ? taskBelow.id : null
          });
        }
      } catch (error) {
        showErrorNotification('Erro ao reordenar tarefa');

        setTasks(prevTasks => {
          const revertedTasks = [...prevTasks];
          const [movedTask] = revertedTasks.splice(newIndex, 1);
          revertedTasks.splice(oldIndex, 0, movedTask);
          return revertedTasks;
        });
      }
    };

    // Ações em massa via endpoint bulk
    const handleBulkAction = async (
      selectedTaskIds,
      operation,
      additionalData = {}
    ) => {
      if (
        !taskTemplateId ||
        !Array.isArray(selectedTaskIds) ||
        selectedTaskIds.length === 0
      ) {
        return;
      }

      try {
        // considera apenas tasks persistidas
        const nonTemporaryTaskIds = selectedTaskIds.filter(taskId => {
          const task = tasks.find(t => t.id === taskId);
          return task && !task.isTemporary;
        });

        if (nonTemporaryTaskIds.length === 0) {
          showErrorNotification(
            'Nenhuma tarefa válida selecionada para ação em massa'
          );
          return;
        }

        const bulkBody = {
          taskTemplateItemIds: nonTemporaryTaskIds,
          operation,
          ...additionalData
        };

        // remove campos opcionais vazios
        if (
          bulkBody.deadlineDays === undefined ||
          bulkBody.deadlineDays === ''
        ) {
          delete bulkBody.deadlineDays;
        }
        // Manter null para "sem prazo"
        if (
          bulkBody.agentId === undefined ||
          bulkBody.agentId === null ||
          bulkBody.agentId === '' ||
          bulkBody.agentId === 0
        ) {
          delete bulkBody.agentId;
        }

        const response = await TaskTemplateItemService.bulk(
          taskTemplateId,
          bulkBody
        );

        if (response && !response.error) {
          // Atualiza o estado local conforme a operação
          setTasks(prev => {
            const updated = prev
              .map(task => {
                if (!nonTemporaryTaskIds.includes(task.id)) return task;
                switch (operation) {
                  case 'COMPLETE':
                    return { ...task, status: true };
                  case 'UNCOMPLETE':
                    return { ...task, status: false };
                  case 'DELETE':
                    return null;
                  case 'SET_DEADLINE':
                    return {
                      ...task,
                      deadlineDays: additionalData.deadlineDays,
                      days_to_complete: additionalData.deadlineDays
                    };
                  case 'SET_AGENT': {
                    const found =
                      agents.find(a => a.id === additionalData.agentId) || null;
                    return found
                      ? {
                          ...task,
                          agent: {
                            id: found.id,
                            firstName: found.firstName || '',
                            lastName: found.lastName || '',
                            name: `${found.firstName || ''} ${
                              found.lastName || ''
                            }`.trim()
                          }
                        }
                      : { ...task, agent: null };
                  }
                  default:
                    return task;
                }
              })
              .filter(Boolean);
            return updated;
          });

          let successMessage = 'Ação em massa aplicada com sucesso';
          switch (operation) {
            case 'COMPLETE':
              successMessage = `${nonTemporaryTaskIds.length} tarefa(s) marcada(s) como concluída(s)`;
              break;
            case 'UNCOMPLETE':
              successMessage = `${nonTemporaryTaskIds.length} tarefa(s) desmarcada(s)`;
              break;
            case 'DELETE':
              successMessage = `${nonTemporaryTaskIds.length} tarefa(s) excluída(s)`;
              break;
            case 'SET_DEADLINE':
              successMessage = `Prazo definido para ${nonTemporaryTaskIds.length} tarefa(s)`;
              break;
            case 'SET_AGENT':
              successMessage = `Responsável definido para ${nonTemporaryTaskIds.length} tarefa(s)`;
              break;
          }
          showSuccessNotification(successMessage);
        } else {
          throw new Error(
            response?.userMessage ||
              response?.message ||
              'Erro ao aplicar ação em massa'
          );
        }
      } catch (error) {
        console.error(error);
        showErrorNotification(error.message || 'Erro ao aplicar ação em massa');
      }
    };

    // Função para obter todas as tarefas formatadas para envio ao backend
    const getTasks = () => {
      return tasks.map(task => ({
        id: task.isTemporary ? null : task.id, // IDs temporários são enviados como null
        title: task.title,
        description: task.description || '',
        status: task.status || false,
        isGroupTitle: task.isGroupTitle || false,
        deadlineDays:
          task.deadlineDays === null ||
          task.deadlineDays === undefined ||
          task.deadlineDays === ''
            ? null
            : parseInt(task.deadlineDays),
        agent: task.agent ? { id: task.agent.id } : null
      }));
    };

    // Função para definir tarefas (usada pelo componente pai)
    const setTasksFromParent = newTasks => {
      console.log('setTasksFromParent chamado com:', newTasks);

      // Sincroniza os campos de prazo nas tasks
      const syncedTasks = (newTasks || []).map(task => {
        const deadlineValue =
          task.deadlineDays !== undefined
            ? task.deadlineDays
            : task.days_to_complete;
        // Garantir que valores vazios sejam tratados como null
        const normalizedValue =
          deadlineValue === null ||
          deadlineValue === undefined ||
          deadlineValue === ''
            ? null
            : deadlineValue;
        return {
          ...task,
          deadlineDays: normalizedValue,
          days_to_complete: normalizedValue
        };
      });

      setTasks(syncedTasks);
      if (newTasks && newTasks.length > 0) {
        const maxId = Math.max(...newTasks.map(t => t.id || 0), 999);
        setNextTaskId(maxId + 1);
      }
      setIsInitialized(true);
    };

    // Expor funções através da ref
    useImperativeHandle(ref, () => ({
      getTasks: () => getTasks(),
      setTasks: setTasksFromParent
    }));

    return (
      <>
        <header className="mb-4">
          <Flex className="w-100" justifyContent="between" alignItems="center">
            <div>
              <h5 className="mb-0">Lista de Tarefas</h5>
              <small className="text-muted">{tasks.length} itens</small>
            </div>

            <Flex alignItems="center" className="gap-2">
              <Dropdown
                as={ButtonGroup}
                align="end"
                className="btn-reveal-trigger"
              >
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAddTaskClick}
                  disabled={showNewTaskForm || isCreatingTask}
                  style={{
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  {isCreatingTask ? 'Adicionando...' : 'Adicionar tarefa'}
                </Button>
                <Dropdown.Toggle
                  variant="primary"
                  id="dropdown-basic"
                  className="px-2"
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                  disabled={showNewTaskForm || isCreatingTask}
                >
                  <FontAwesomeIcon icon={faChevronDown} size="sm" />
                </Dropdown.Toggle>

                <Dropdown.Menu style={{ width: '250px' }}>
                  <Dropdown.Item className="w-100" onClick={handleAddTaskClick}>
                    <strong className="fs-9 d-block">Tarefa</strong>
                    <span
                      className="text-600 w-100 text-break"
                      style={{ whiteSpace: 'normal' }}
                    >
                      São atividades específicas que podem ser organizadas e
                      acompanhadas
                    </span>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleGroupTitleClick}>
                    <strong className="fs-9 d-block">Título</strong>
                    <span
                      className="text-600 text-break"
                      style={{ whiteSpace: 'normal' }}
                    >
                      Insira um título para agrupar visualmente as tarefas
                    </span>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Flex>
          </Flex>
        </header>

        <div>
          {showNewTaskForm && (
            <TemplateNewTaskCard
              onCancel={handleCancelNewTask}
              onTaskAdded={handleTaskAdded}
              onTaskAddedSuccess={handleKeepFormOpen}
              isGroupTitle={isGroupTitle}
              agents={agents}
              isLoading={isCreatingTask}
            />
          )}

          {tasks.length === 0 && !showNewTaskForm ? (
            <EmptyList
              imgSize={250}
              title="Nenhuma tarefa criada"
              description="Comece criando sua primeira tarefa usando o botão acima"
            />
          ) : (
            <SimpleTaskList
              tasks={tasks}
              agents={agents || []} // Fallback para array vazio
              onTaskDelete={handleTaskDelete}
              onTaskReorder={handleTaskReorder}
              onTaskUpdate={handleTaskUpdate}
              showBulkActions={true}
              onBulkAction={handleBulkAction}
            />
          )}
        </div>
      </>
    );
  }
);

TemplateTaskSection.displayName = 'TemplateTaskSection';

export default TemplateTaskSection;
